import React, { useState, useEffect } from 'react';
import { Settings, LogOut, Lock, AlertCircle } from 'lucide-react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';

const auth = getAuth();
const db = getFirestore();

const AdminHome = ({ setShowAdminDashboard, handleLogout }) => {
  const [displayName, setDisplayName] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(true); // État de l'école
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // 1. Récupération du profil utilisateur
          const userDocRef = doc(db, 'users', user.uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();

            // Vérification statut utilisateur
            if (userData.status === 'inactive') {
              setError("Votre compte est inactif. Accès refusé.");
              setLoading(false);
              handleLogout();
              return;
            }
            
            setUserRole(userData.role || 'utilisateur');
            
            // Définition du nom à afficher
            if (userData.name && userData.name.fullName) {
              setDisplayName(userData.name.fullName);
            } else if (userData.username) {
              setDisplayName(userData.username);
            } else {
              setDisplayName(user.displayName || user.email);
            }

            // 2. NOUVEAU : Vérification de l'abonnement de l'école
            // On ne vérifie pas pour le superAdmin
            if (userData.schoolId && userData.role !== 'superAdmin') {
              const schoolDocRef = doc(db, 'schools', userData.schoolId);
              const schoolSnap = await getDoc(schoolDocRef);
              
              if (schoolSnap.exists()) {
                const schoolData = schoolSnap.data();
                setIsSubscriptionActive(schoolData.subscriptionActive !== false);
              }
            }

          } else {
            setError("Profil utilisateur introuvable.");
            setLoading(false);
            handleLogout();
            return;
          }
        } catch (e) {
          console.error("Erreur Firestore:", e);
          setError("Échec du chargement du profil.");
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, [handleLogout]); 

  // --- RENDU ---

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <p className="text-gray-700 text-lg">Chargement de votre profil...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 p-4">
        <p className="text-red-600 text-lg mb-4">Erreur: {error}</p>
        <button onClick={handleLogout} className="bg-red-600 text-white py-2.5 px-5 rounded-lg font-semibold">
          Déconnexion
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100 p-4 font-sans antialiased">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl overflow-hidden p-8 text-center">
        
        {/* BANDEAU D'ALERTE SI ABONNEMENT EXPIRE */}
        {!isSubscriptionActive && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center text-amber-700 text-left">
            <AlertCircle className="mr-3 shrink-0" size={24} />
            <p className="text-sm font-medium">
              <strong>Abonnement expiré :</strong> L'accès aux outils d'administration de votre école est restreint. Veuillez régulariser le paiement.
            </p>
          </div>
        )}

        <h2 className="text-3xl font-bold text-gray-800 mb-4">
          Bienvenue, {displayName} !
        </h2>
        <p className="text-gray-600 mb-6">
          Votre rôle actuel est : <span className="font-semibold text-blue-600">{userRole}</span>
        </p>

        <div className="space-y-4">
          {/* BOUTON DASHBOARD : Affiché seulement si l'abonnement est actif */}
          {isSubscriptionActive ? (
            <button
              onClick={() => setShowAdminDashboard(true)}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold text-lg hover:bg-blue-700 transition duration-200 transform hover:scale-105 flex items-center justify-center mx-auto"
            >
              <Settings size={20} className="mr-2" />
              Aller au Tableau de Bord Admin
            </button>
          ) : (
            /* VERSION VERROUILLÉE DU BOUTON */
            <div className="w-full bg-gray-200 text-gray-500 py-3 px-6 rounded-lg font-semibold text-lg flex items-center justify-center mx-auto cursor-not-allowed border border-gray-300">
              <Lock size={20} className="mr-2" />
              Dashboard Verrouillé
            </div>
          )}

          {/* BOUTON DÉCONNEXION : Toujours disponible */}
          <button
            onClick={handleLogout}
            className="w-full bg-red-600 text-white py-2.5 px-5 rounded-lg font-semibold text-base hover:bg-red-700 transition duration-200 transform hover:scale-105 flex items-center justify-center mx-auto"
          >
            <LogOut size={18} className="mr-2" />
            Déconnexion
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminHome;